// src/contexts/LicenseContext.js
// Provides organization license data and integration/evidence assignment status for conditional feature visibility
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "./AuthContext";

const LicenseContext = createContext();

export const LicenseProvider = ({ children }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isIntegrationAssigned, setIsIntegrationAssigned] = useState(false);
  const [isEvidenceAssigned, setIsEvidenceAssigned] = useState(false);
  const { isAuthenticated, authVersion } = useAuth();

  // Fetch licenses from API
  const fetchLicenses = useCallback(async () => {
    // Check if we have a selected organization with a valid organizationId
    const selectedOrg = localStorage.getItem("selectedOrganization");
    if (!selectedOrg) {
      setLicenses([]);
      return;
    }
    try {
      const org = JSON.parse(selectedOrg);
      if (!org.organizationId) {
        setLicenses([]);
        return;
      }
    } catch {
      setLicenses([]);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get("/Organizations/licenses");
      setLicenses(response.data);
    } catch (error) {
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch integration assignment status for the current user
  const fetchIntegrationAssignment = useCallback(async () => {
    const selectedOrg = localStorage.getItem("selectedOrganization");
    if (!selectedOrg) {
      setIsIntegrationAssigned(false);
      return;
    }
    try {
      const org = JSON.parse(selectedOrg);
      if (!org.organizationId) {
        setIsIntegrationAssigned(false);
        return;
      }
    } catch {
      setIsIntegrationAssigned(false);
      return;
    }

    try {
      const response = await api.get("/integration-assignments/me");
      setIsIntegrationAssigned(response.data?.isAssigned === true);
    } catch {
      setIsIntegrationAssigned(false);
    }
  }, []);

  // Fetch whether the current user has any GRC evidence requests assigned to them.
  // Unlocks the assignee-only GRC view for users below org-level Risk Manager.
  // 404 (Features:GrcReadinessV2 off) and auth errors both resolve to "not assigned".
  const fetchEvidenceAssignment = useCallback(async () => {
    const selectedOrg = localStorage.getItem("selectedOrganization");
    if (!selectedOrg) {
      setIsEvidenceAssigned(false);
      return;
    }
    try {
      const org = JSON.parse(selectedOrg);
      if (!org.organizationId) {
        setIsEvidenceAssigned(false);
        return;
      }
    } catch {
      setIsEvidenceAssigned(false);
      return;
    }

    try {
      const response = await api.get("/security-insights/evidence/requests/my");
      setIsEvidenceAssigned(Array.isArray(response.data) && response.data.length > 0);
    } catch {
      setIsEvidenceAssigned(false);
    }
  }, []);

  // Check if organization has a specific license by product name
  const hasLicense = useCallback((productName) => {
    return licenses.some(
      (license) => license.productName?.toLowerCase() === productName?.toLowerCase()
    );
  }, [licenses]);

  // Fetch when authenticated and have a selected org with valid organizationId
  useEffect(() => {
    const selectedOrg = localStorage.getItem("selectedOrganization");
    if (!isAuthenticated || !selectedOrg) return;
    try {
      const org = JSON.parse(selectedOrg);
      if (!org.organizationId) return;
    } catch {
      return;
    }
    fetchLicenses();
    fetchIntegrationAssignment();
    fetchEvidenceAssignment();
  }, [isAuthenticated, authVersion, fetchLicenses, fetchIntegrationAssignment, fetchEvidenceAssignment]);

  return (
    <LicenseContext.Provider value={{ licenses, loading, hasLicense, isIntegrationAssigned, isEvidenceAssigned, refetchLicenses: fetchLicenses, refetchIntegrationAssignment: fetchIntegrationAssignment, refetchEvidenceAssignment: fetchEvidenceAssignment }}>
      {children}
    </LicenseContext.Provider>
  );
};

// Hook to get all licenses
export const useLicenses = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useLicenses must be used within a LicenseProvider");
  }
  return context.licenses;
};

// Hook to check if org has a license
export const useHasLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useHasLicense must be used within a LicenseProvider");
  }
  return context.hasLicense;
};

// Hook to get license loading state
export const useLicenseLoading = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useLicenseLoading must be used within a LicenseProvider");
  }
  return context.loading;
};

// Hook to refetch licenses (e.g., after org selection)
export const useRefetchLicenses = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useRefetchLicenses must be used within a LicenseProvider");
  }
  return context.refetchLicenses;
};

// Hook to check if current user is assigned to manage integrations
export const useIsIntegrationAssigned = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useIsIntegrationAssigned must be used within a LicenseProvider");
  }
  return context.isIntegrationAssigned;
};

// Hook to refetch integration assignment status
export const useRefetchIntegrationAssignment = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useRefetchIntegrationAssignment must be used within a LicenseProvider");
  }
  return context.refetchIntegrationAssignment;
};

// Hook to check if current user has GRC evidence requests assigned to them
export const useIsEvidenceAssigned = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useIsEvidenceAssigned must be used within a LicenseProvider");
  }
  return context.isEvidenceAssigned;
};
